/**
 * Compose audience builder — pure selection logic for the Recipients panel.
 * (SILL-386 T3 feedback round 2)
 *
 * Model: audience PRESET (email intent, not raw DB fields) → REFINEMENTS
 * (include kind, timeline, verification, tags) → EXCLUSIONS → matched list.
 *
 * Fail-closed rule: a refinement on a dimension a recipient KIND lacks
 * excludes that kind entirely. Contacts have no timeline and no verification
 * tier, so a timeline or tier refinement (or "Exclude unverified") drops all
 * contacts — this is a send-audience builder, not a browse filter, and the
 * safe reading of "Verified only" is "nobody unverifiable receives this".
 *
 * Reachability: contacts without an email cannot receive; linked contacts ARE
 * clients (they deliver via the client branch and must not bypass the
 * client-side outreach permission) — both are structurally unreachable and
 * are surfaced as disabled rows, never selectable.
 */
import type { CampaignAudienceSpec } from "./api";
import type { ClientVM, ContactVM } from "./view-model";
import {
  isTierKnown,
  passesTierFilter,
  passesTimelineFilter,
  type TierFilterId,
  type TimelineBucketId,
} from "./audience-filters";

// ─────────────────────────────────────────────────────────────────────────────
// Presets
// ─────────────────────────────────────────────────────────────────────────────

export type AudiencePresetId =
  | "everyone"
  | "active"
  | "needs"
  | "ready"
  | "cold"
  | "unverified";

export interface AudiencePresetDef {
  id: AudiencePresetId;
  /** Chip label — buyer-facing email intent. */
  label: string;
  clientPred: (c: ClientVM) => boolean;
  contactPred: (ct: ContactVM) => boolean;
}

/** Minutes after which a client is "going cold" (mirrors lib/audience-filters). */
const COLD_MINUTES = 4320;

/**
 * Intent-named presets. Contact predicates map each intent onto the contact
 * funnel where it has a defensible meaning, and fail closed where it doesn't
 * (a contact has no timeline, so "Ready soon" is clients-only).
 */
export const AUDIENCE_PRESETS: AudiencePresetDef[] = [
  {
    id: "everyone",
    label: "Everyone",
    clientPred: () => true,
    contactPred: () => true,
  },
  {
    id: "active",
    label: "All active",
    clientPred: (c) => c.activeThisWeek,
    // Contacts engaging with Sill (opened the invite or signed up).
    contactPred: (ct) =>
      ct.funnelState === "opened" || ct.funnelState === "signed_up",
  },
  {
    id: "needs",
    label: "Needs follow-up",
    clientPred: (c) => c.needsResponse,
    // Contacts you haven't converted yet — added but never invited, or
    // invited with no movement.
    contactPred: (ct) =>
      ct.funnelState === "added" ||
      ct.funnelState === "invited" ||
      ct.funnelState === "unlinked",
  },
  {
    id: "ready",
    label: "Ready soon",
    clientPred: (c) => c.timelineShort === "0–3 mo",
    contactPred: () => false,
  },
  {
    id: "cold",
    label: "Going cold",
    clientPred: (c) => c.minutesAgo >= COLD_MINUTES,
    contactPred: () => false,
  },
  {
    id: "unverified",
    label: "Missing verification",
    // A hidden tier (financials locked) is unknown, not self-reported.
    clientPred: (c) => isTierKnown(c) && c.tier === 1,
    // Contacts aren't on Sill — "verify" isn't actionable for them.
    contactPred: () => false,
  },
];

// ─────────────────────────────────────────────────────────────────────────────
// Audience state
// ─────────────────────────────────────────────────────────────────────────────

export type IncludeKind = "both" | "clients" | "contacts";

export interface AudienceState {
  preset: AudiencePresetId;
  include: IncludeKind;
  timeline: TimelineBucketId;
  tier: TierFilterId;
  /** Contact tags — a contact matches when it carries ANY selected tag. */
  tags: string[];
  /** Exclusion chip: drop tier-1 clients AND all contacts (unverifiable). */
  excludeUnverified: boolean;
  search: string;
}

export const DEFAULT_AUDIENCE: AudienceState = {
  preset: "everyone",
  include: "both",
  timeline: "any",
  tier: "any",
  tags: [],
  excludeUnverified: false,
  search: "",
};

// ─────────────────────────────────────────────────────────────────────────────
// Matching
// ─────────────────────────────────────────────────────────────────────────────

/** A contact that can actually receive a broadcast as a contact. */
export function isContactReachable(ct: ContactVM): boolean {
  return Boolean(ct.email) && ct.funnelState !== "linked";
}

export interface MatchedAudience {
  clients: ClientVM[];
  /** Matched AND reachable — the selectable contact set. */
  contacts: ContactVM[];
  /**
   * Contacts that match the audience but can't receive (no email / linked).
   * Rendered as disabled rows with a reason so the agent sees why someone
   * they searched for isn't selectable.
   */
  unreachableContacts: ContactVM[];
}

function clientMatches(c: ClientVM, s: AudienceState, preset: AudiencePresetDef): boolean {
  if (s.include === "contacts") return false;
  if (!preset.clientPred(c)) return false;
  if (!passesTimelineFilter(c, s.timeline)) return false;
  if (!passesTierFilter(c, s.tier)) return false;
  if (s.excludeUnverified && (!isTierKnown(c) || c.tier === 1)) return false;
  const q = s.search.trim().toLowerCase();
  if (q && !c.name.toLowerCase().includes(q)) return false;
  return true;
}

function contactMatches(ct: ContactVM, s: AudienceState, preset: AudiencePresetDef): boolean {
  if (s.include === "clients") return false;
  // Fail closed: client-only dimensions exclude contacts entirely.
  if (s.timeline !== "any") return false;
  if (s.tier !== "any") return false;
  if (s.excludeUnverified) return false;
  if (!preset.contactPred(ct)) return false;
  if (s.tags.length > 0 && !s.tags.some((t) => ct.tags.includes(t))) return false;
  const q = s.search.trim().toLowerCase();
  if (
    q &&
    !ct.name.toLowerCase().includes(q) &&
    !(ct.email ?? "").toLowerCase().includes(q)
  ) {
    return false;
  }
  return true;
}

export function matchAudience(
  clients: ClientVM[],
  contacts: ContactVM[],
  state: AudienceState,
): MatchedAudience {
  const preset =
    AUDIENCE_PRESETS.find((p) => p.id === state.preset) ?? AUDIENCE_PRESETS[0];
  const matchedContacts = contacts.filter((ct) =>
    contactMatches(ct, state, preset),
  );
  return {
    clients: clients.filter((c) => clientMatches(c, state, preset)),
    contacts: matchedContacts.filter(isContactReachable),
    unreachableContacts: matchedContacts.filter((ct) => !isContactReachable(ct)),
  };
}

/**
 * Reachable-recipient count per preset, holding every other refinement
 * constant — what each chip would match if clicked right now.
 */
export function presetCounts(
  clients: ClientVM[],
  contacts: ContactVM[],
  state: AudienceState,
): Record<AudiencePresetId, number> {
  const out = {} as Record<AudiencePresetId, number>;
  for (const p of AUDIENCE_PRESETS) {
    const m = matchAudience(clients, contacts, { ...state, preset: p.id });
    out[p.id] = m.clients.length + m.contacts.length;
  }
  return out;
}

// ─────────────────────────────────────────────────────────────────────────────
// Summary tokens — "Sending to: Needs follow-up · Verified only · 0–3 mo"
// ─────────────────────────────────────────────────────────────────────────────

export interface AudienceToken {
  /** Which state dimension the token edits/removes. */
  key:
    | "preset"
    | "include"
    | "timeline"
    | "tier"
    | "excludeUnverified"
    | `tag:${string}`
    | "search";
  label: string;
  /** Removable tokens reset their dimension to the default. */
  removable: boolean;
}

export function audienceTokens(state: AudienceState): AudienceToken[] {
  const tokens: AudienceToken[] = [];
  const preset =
    AUDIENCE_PRESETS.find((p) => p.id === state.preset) ?? AUDIENCE_PRESETS[0];
  tokens.push({
    key: "preset",
    label: preset.label,
    removable: preset.id !== "everyone",
  });
  if (state.include === "clients") {
    tokens.push({ key: "include", label: "Connected only", removable: true });
  } else if (state.include === "contacts") {
    tokens.push({ key: "include", label: "Not yet on Sill", removable: true });
  }
  if (state.tier === "bank") {
    tokens.push({ key: "tier", label: "Verified only", removable: true });
  } else if (state.tier === "doc") {
    tokens.push({ key: "tier", label: "Document only", removable: true });
  }
  if (state.timeline !== "any") {
    tokens.push({ key: "timeline", label: state.timeline, removable: true });
  }
  for (const t of state.tags) {
    tokens.push({ key: `tag:${t}`, label: `#${t}`, removable: true });
  }
  if (state.excludeUnverified) {
    tokens.push({
      key: "excludeUnverified",
      label: "Excl. unverified",
      removable: true,
    });
  }
  if (state.search.trim()) {
    tokens.push({
      key: "search",
      label: `"${state.search.trim()}"`,
      removable: true,
    });
  }
  return tokens;
}

// ─────────────────────────────────────────────────────────────────────────────
// Backend filter mapping — AudienceState → messaging.SegmentFilter wire shape
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Wire shape of the backend `messaging.SegmentFilter`. The Go struct carries NO
 * json tags, so the encoder emits exported field names verbatim (capitalized).
 * Without a hand-pick this filter IS the campaign audience. With one
 * (`explicit: true`, see buildCampaignAudienceSpec) the campaign sends to
 * exactly the picks and the filter is kept only to re-open the builder.
 *
 * The server applies every dimension through `crm.MatchesSegment`, the one
 * definition of each preset and refinement; `matchAudience` mirrors it, and
 * backend/internal/services/crm/testdata/audience_preset_fixture.json pins the
 * two (including this encoding: timeline bucket ids, tier chip ids).
 */
export interface SegmentFilterWire {
  Preset: AudiencePresetId;
  IncludeKind?: IncludeKind;
  Timeline?: TimelineBucketId;
  Tier?: TierFilterId;
  Tags?: string[];
  ExcludeUnverified?: boolean;
  Search?: string;
}

/**
 * Map the FE audience builder state onto the backend SegmentFilter wire shape.
 * Pure and unit-tested. Default/empty dimensions are omitted so the payload
 * stays minimal and a round-tripped campaign re-hydrates to a clean state.
 *
 * The mapping is fail-closed-preserving by construction: a non-default
 * Timeline/Tier/ExcludeUnverified is forwarded as-is, so the backend resolver
 * applies the same contact-exclusion rule the FE preview shows.
 */
export function audienceStateToFilter(state: AudienceState): SegmentFilterWire {
  const filter: SegmentFilterWire = { Preset: state.preset };
  if (state.include !== "both") filter.IncludeKind = state.include;
  if (state.timeline !== "any") filter.Timeline = state.timeline;
  if (state.tier !== "any") filter.Tier = state.tier;
  if (state.tags.length > 0) filter.Tags = [...state.tags];
  if (state.excludeUnverified) filter.ExcludeUnverified = true;
  const search = state.search.trim();
  if (search) filter.Search = search;
  return filter;
}

/**
 * An explicit per-recipient selection from the Recipients panel — the clients
 * and contacts the agent toggled on by hand.
 */
export interface ExplicitSelection {
  clientIds: string[];
  contactIds: string[];
}

/**
 * Build the campaign audience wire spec from the audience-builder state plus any
 * explicit per-recipient selection.
 *
 * When the agent has toggled specific recipients, the campaign sends to EXACTLY
 * those people — the backend Enrollment / explicit-list shape, signalled by
 * `explicit: true`. This is the fix for "I selected a few users but it sent to
 * everyone": the selection previously drove only the preview while the filter
 * cohort always shipped (and the backend `ResolveCohort` only ever APPENDS
 * `include` to the filter set, so it could never narrow). With `explicit` set,
 * the send path resolves the include set alone.
 *
 * With no explicit selection the filter-defined cohort ships (the live segment
 * that reflects who qualifies at send time).
 */
/**
 * Routes the composer's picks onto the refs a campaign names them by. A client
 * whose profile id is hidden (financials locked) is keyed by contact id, so it
 * is sent as a contact pick; the backend resolves a connected person named by
 * contact id to the client, and validates it as the pro's own contact.
 */
export function campaignPickSelection(
  clients: ClientVM[],
  selection: ExplicitSelection,
): ExplicitSelection {
  const byContact = new Set(clients.filter((c) => c.pickByContactId).map((c) => c.id));
  return {
    clientIds: selection.clientIds.filter((id) => !byContact.has(id)),
    contactIds: [...selection.contactIds, ...selection.clientIds.filter((id) => byContact.has(id))],
  };
}

export function buildCampaignAudienceSpec(
  state: AudienceState,
  selection: ExplicitSelection,
): CampaignAudienceSpec {
  const filter = audienceStateToFilter(state);
  const include = [
    ...selection.clientIds.map((id) => ({ ProfileID: id })),
    ...selection.contactIds.map((id) => ({ ContactID: id })),
  ];
  if (include.length === 0) return { filter };
  return { filter, include, explicit: true };
}

/**
 * Reverse of `audienceStateToFilter`: rehydrate a stored SegmentFilter back into
 * a full AudienceState for editing a saved draft. Missing/omitted dimensions
 * fall back to DEFAULT_AUDIENCE, so a minimal persisted filter re-opens as a
 * clean builder. Pure and unit-tested; the round-trip
 * `filterToAudienceState(audienceStateToFilter(s))` is the identity on every
 * canonical state.
 */
export function filterToAudienceState(
  // Accepts both the FE `SegmentFilterWire` and the looser persisted
  // `CampaignSegmentFilter` (whose fields are plain strings). Stored values are
  // cast back to the builder's enums — a display/edit mapping, so an unexpected
  // string falls through to the default via the enum cast + ?? guard.
  filter:
    | {
        Preset?: string;
        IncludeKind?: string;
        Timeline?: string;
        Tier?: string;
        Tags?: string[];
        ExcludeUnverified?: boolean;
        Search?: string;
      }
    | undefined,
): AudienceState {
  if (!filter) return { ...DEFAULT_AUDIENCE };
  // Coerce with `||`, NOT `??`: the backend SegmentFilter has no `omitempty`, so
  // omitted enum dimensions round-trip as EMPTY STRINGS ("") — which `??` would
  // pass through unchanged. An empty Preset/IncludeKind/Timeline/Tier is never a
  // valid builder value, and leaving Timeline/Tier "" fail-closes the whole
  // audience (every recipient excluded). Empty must mean "default".
  return {
    preset: (filter.Preset || DEFAULT_AUDIENCE.preset) as AudiencePresetId,
    include: (filter.IncludeKind || DEFAULT_AUDIENCE.include) as IncludeKind,
    timeline: (filter.Timeline || DEFAULT_AUDIENCE.timeline) as TimelineBucketId,
    tier: (filter.Tier || DEFAULT_AUDIENCE.tier) as TierFilterId,
    tags: filter.Tags ? [...filter.Tags] : [],
    excludeUnverified: filter.ExcludeUnverified ?? false,
    search: filter.Search ?? "",
  };
}

/**
 * Human cohort label for a preset id — the buyer-facing intent name shown on a
 * campaign card and in the send bar (e.g. "Going cold"). Unknown ids fall back
 * to "Everyone".
 */
export function cohortLabel(preset: AudiencePresetId | string | undefined): string {
  return (
    AUDIENCE_PRESETS.find((p) => p.id === preset)?.label ??
    AUDIENCE_PRESETS[0].label
  );
}

/** Remove/reset one token's dimension; returns the next state. */
export function removeToken(
  state: AudienceState,
  key: AudienceToken["key"],
): AudienceState {
  if (key === "preset") return { ...state, preset: "everyone" };
  if (key === "include") return { ...state, include: "both" };
  if (key === "timeline") return { ...state, timeline: "any" };
  if (key === "tier") return { ...state, tier: "any" };
  if (key === "excludeUnverified") return { ...state, excludeUnverified: false };
  if (key === "search") return { ...state, search: "" };
  if (key.startsWith("tag:")) {
    const tag = key.slice(4);
    return { ...state, tags: state.tags.filter((t) => t !== tag) };
  }
  return state;
}
